import type { GameplayCue } from '../flow/types'

const requiredElement = <T extends HTMLElement>(selector: string): T => {
  const element = document.querySelector<T>(selector)
  if (!element) throw new Error(`Missing required objective UI element: ${selector}`)
  return element
}

type GameplayTutorial = NonNullable<GameplayCue['tutorial']>
type GameplayFeedbackTone = 'miss' | 'hint'

export class ObjectiveOverlay {
  private readonly panel = requiredElement<HTMLElement>('#objective-panel')
  private readonly label = requiredElement<HTMLElement>('#objective-label')
  private readonly title = requiredElement<HTMLElement>('#objective-title')
  private readonly instructions = requiredElement<HTMLElement>('#objective-instructions')
  private readonly tutorial = requiredElement<HTMLElement>('#gameplay-tutorial')
  private readonly tutorialTitle = requiredElement<HTMLElement>('#gameplay-tutorial-title')
  private readonly tutorialInstructions = requiredElement<HTMLUListElement>(
    '#gameplay-tutorial-instructions',
  )
  private readonly confirmButton = requiredElement<HTMLButtonElement>('#gameplay-tutorial-confirm')
  private readonly feedback = requiredElement<HTMLElement>('#gameplay-feedback')
  private onTutorialConfirmed: (() => void) | null = null
  private completionTimer: number | null = null
  private feedbackTimer: number | null = null

  public constructor() {
    this.confirmButton.addEventListener('click', this.handleTutorialConfirmed)
  }

  public showObjective(title: string, instructions?: string): void {
    this.clearCompletionTimer()
    this.hideFeedback()
    this.panel.dataset.visible = 'true'
    this.panel.dataset.state = 'active'
    this.panel.setAttribute('aria-hidden', 'false')
    this.label.textContent = 'Objective'
    this.title.textContent = title.trim()
    this.instructions.textContent = instructions?.trim() ?? ''
    this.instructions.hidden = this.instructions.textContent.length === 0
  }

  public updateProgress(instructions: string): void {
    this.instructions.textContent = instructions.trim()
    this.instructions.hidden = this.instructions.textContent.length === 0
  }

  public showTutorial(tutorial: GameplayTutorial, onConfirmed: () => void): void {
    this.onTutorialConfirmed = onConfirmed
    this.tutorialTitle.textContent = tutorial.title.trim()
    this.confirmButton.textContent = tutorial.confirmLabel?.trim() || 'Got it'
    this.tutorialInstructions.replaceChildren(
      ...tutorial.instructions.map((instruction) => {
        const item = document.createElement('li')
        item.textContent = instruction.trim()
        return item
      }),
    )
    this.tutorial.dataset.visible = 'true'
    this.tutorial.setAttribute('aria-hidden', 'false')
    this.confirmButton.focus({ preventScroll: true })
  }

  public complete(title: string): void {
    this.clearCompletionTimer()
    this.hideFeedback()
    this.panel.dataset.visible = 'true'
    this.panel.dataset.state = 'complete'
    this.panel.setAttribute('aria-hidden', 'false')
    this.label.textContent = 'Objective complete'
    this.title.textContent = title.trim()
    this.instructions.textContent = 'Key found'
    this.instructions.hidden = false
    this.completionTimer = window.setTimeout(() => {
      this.hideObjective()
      this.completionTimer = null
    }, 2600)
  }

  public hideObjective(): void {
    this.panel.dataset.visible = 'false'
    this.panel.setAttribute('aria-hidden', 'true')
  }

  public showFeedback(
    text: string,
    clientX: number,
    clientY: number,
    tone: GameplayFeedbackTone = 'miss',
  ): void {
    this.clearFeedbackTimer()
    const horizontalMargin = Math.min(84, window.innerWidth * 0.24)
    const verticalMargin = 52
    const left = Math.max(horizontalMargin, Math.min(window.innerWidth - horizontalMargin, clientX))
    const top = Math.max(verticalMargin, Math.min(window.innerHeight - 22, clientY - 16))

    this.feedback.textContent = text.trim()
    this.feedback.style.left = `${left}px`
    this.feedback.style.top = `${top}px`
    this.feedback.dataset.tone = tone
    this.feedback.dataset.visible = 'false'
    this.feedback.setAttribute('aria-hidden', 'false')
    // Restart the comic pop even when consecutive misses use identical copy.
    void this.feedback.offsetWidth
    this.feedback.dataset.visible = 'true'
    this.feedbackTimer = window.setTimeout(() => {
      this.hideFeedback()
    }, tone === 'hint' ? 1900 : 950)
  }

  private readonly handleTutorialConfirmed = (): void => {
    const onConfirmed = this.onTutorialConfirmed
    this.onTutorialConfirmed = null
    this.tutorial.dataset.visible = 'false'
    this.tutorial.setAttribute('aria-hidden', 'true')
    onConfirmed?.()
  }

  private clearCompletionTimer(): void {
    if (this.completionTimer === null) return
    window.clearTimeout(this.completionTimer)
    this.completionTimer = null
  }

  private clearFeedbackTimer(): void {
    if (this.feedbackTimer === null) return
    window.clearTimeout(this.feedbackTimer)
    this.feedbackTimer = null
  }

  private hideFeedback(): void {
    this.clearFeedbackTimer()
    this.feedback.dataset.visible = 'false'
    this.feedback.setAttribute('aria-hidden', 'true')
  }

  public dispose(): void {
    this.confirmButton.removeEventListener('click', this.handleTutorialConfirmed)
    this.clearCompletionTimer()
    this.hideFeedback()
    this.onTutorialConfirmed = null
    this.tutorial.dataset.visible = 'false'
    this.tutorial.setAttribute('aria-hidden', 'true')
    this.hideObjective()
  }
}
